/**
 * Increment Recommendations page — role-aware.
 *
 * Role        | Sees                          | Can edit
 * ------------|-------------------------------|-----------------------------
 * hod         | own dept only, cols 1-45      | HOD cols (41-45)
 * hr          | all depts,    cols 1-50       | HR cols  (46-50), prefilled from HOD
 * management  | all depts,    cols 1-55       | Mgmt cols(51-55), prefilled from HR
 * admin       | all depts,    cols 1-55       | all editable cols
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cyclesAPI, usersAPI } from '../services/api';
import toast from 'react-hot-toast';

/* ── Icons ─────────────────────────────────────────────────────────────── */
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

/* ── Palette matching Excel output ─────────────────────────────────────── */
const C = { blue:'#1A2744', green:'#047857', amber:'#B45309', pink:'#BE185D', orange:'#C2410C', purple:'#6D28D9' };

/* ── Role helpers ────────────────────────────────────────────────────────── */
const ROLE_LABELS = { hod:'HOD', hr:'HR Head', management:'Management', admin:'Admin' };
function canEditHod(role)  { return role === 'hod'  || role === 'admin'; }
function canEditHr(role)   { return role === 'hr'   || role === 'admin'; }
function canEditMgmt(role) { return role === 'management' || role === 'admin'; }
function canSeeHr(role)    { return role === 'hr'   || role === 'management' || role === 'admin'; }
function canSeeMgmt(role)  { return role === 'management' || role === 'admin'; }

/* ── Formatters ──────────────────────────────────────────────────────────── */
const fmtCurr = v => {
  if (v == null || v === '') return '—';
  const n = Number(v); return isNaN(n) ? '—' : '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};
const fmtPctFrac = v => {           // stored as fraction e.g. 0.0842 → 8.42 %
  if (v == null || v === '') return '—';
  const n = Number(v); return isNaN(n) ? '—' : (n * 100).toFixed(2) + '%';
};
const fmtNum = (v, dp = 1) => {
  if (v == null || v === '') return '—';
  const n = Number(v); return isNaN(n) ? '—' : n.toFixed(dp);
};
const fmtDate = v => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); }
  catch { return v; }
};
const fmtRank = v => {
  if (v == null || v === '') return '—';
  const n = Number(v); return isNaN(n) ? '—' : (n * 100).toFixed(0) + '%';
};

/* ── Row init ────────────────────────────────────────────────────────────── */
function nz(raw) {                   // non-zero string, '' when null/0
  if (raw == null || raw === '') return '';
  const n = Number(raw); return (isNaN(n) || n === 0) ? '' : String(n);
}
function initRow(rec) {
  const ineligible = rec.increment_pct_eligibility === 'N';

  // ── Step 1: effective HOD values ──────────────────────────────────────────
  let hodPct, hodIncr, hodNet;
  const hodDesig = rec.hod_designation_change || '';
  const hodComm  = rec.hod_comments           || '';

  if (ineligible) {
    // N rows: default 0% / 0 increment / current gross as net
    hodPct  = rec.hod_recommendation_pct       == null ? '0' : nz(rec.hod_recommendation_pct);
    hodIncr = rec.hod_recommendation_increment == null ? '0' : nz(rec.hod_recommendation_increment);
    hodNet  = rec.hod_recommendation_net       == null
      ? (rec.current_grand_gross ? String(Math.round(Number(rec.current_grand_gross))) : '')
      : nz(rec.hod_recommendation_net);
  } else if (rec.hod_recommendation_pct == null) {
    // Y rows — not yet saved: seed from Incr% Prorated
    const proratedPct = rec.increment_pct_prorated != null
      ? (Number(rec.increment_pct_prorated) * 100).toFixed(2)
      : '';
    const { incr, net } = fromPct(proratedPct, rec.current_grand_gross);
    hodPct  = proratedPct;
    hodIncr = incr;
    hodNet  = net;
  } else {
    hodPct  = nz(rec.hod_recommendation_pct);
    hodIncr = nz(rec.hod_recommendation_increment);
    hodNet  = nz(rec.hod_recommendation_net);
  }

  // ── Step 2: effective HR values — cascade from HOD when not yet saved ────
  const hrNotSaved = rec.hr_recommendation_pct == null;
  const hrPct   = hrNotSaved ? hodPct  : nz(rec.hr_recommendation_pct);
  const hrIncr  = hrNotSaved ? hodIncr : nz(rec.hr_recommendation_increment);
  const hrNet   = hrNotSaved ? hodNet  : nz(rec.hr_recommendation_net);
  const hrDesig = hrNotSaved ? hodDesig : (rec.hr_designation_change || '');
  const hrComm  = hrNotSaved ? hodComm  : (rec.hr_comments           || '');

  // ── Step 3: effective Mgmt values — cascade from HR when not yet saved ───
  const mgNotSaved = rec.mgmt_recommendation_pct == null;
  const mgPct   = mgNotSaved ? hrPct  : nz(rec.mgmt_recommendation_pct);
  const mgIncr  = mgNotSaved ? hrIncr : nz(rec.mgmt_recommendation_increment);
  const mgNet   = mgNotSaved ? hrNet  : nz(rec.mgmt_recommendation_net);
  const mgDesig = mgNotSaved ? hrDesig : (rec.mgmt_designation_change || '');
  const mgComm  = mgNotSaved ? hrComm  : (rec.mgmt_comments           || '');

  return {
    ...rec,
    hod_pct:       hodPct,
    hod_increment: hodIncr,
    hod_net:       hodNet,
    hod_designation_change: hodDesig,
    hod_comments:           hodComm,

    hr_pct:       hrPct,
    hr_increment: hrIncr,
    hr_net:       hrNet,
    hr_designation_change: hrDesig,
    hr_comments:           hrComm,

    mgmt_pct:       mgPct,
    mgmt_increment: mgIncr,
    mgmt_net:       mgNet,
    mgmt_designation_change: mgDesig,
    mgmt_comments:           mgComm,

    out_of_turn: rec.out_of_turn || false,
    dirty: false, saving: false, saved: false,
  };
}

/* ── Two-way calc ────────────────────────────────────────────────────────── */
function fromPct(pct, cgg)  {
  const p = parseFloat(pct), g = Number(cgg);
  if (isNaN(p)||!g) return { incr:'', net:'' };
  const i = Math.round(p/100*g); return { incr:String(i), net:String(Math.round(g+i)) };
}
function fromIncr(incr, cgg) {
  const i = parseFloat(incr), g = Number(cgg);
  if (isNaN(i)||!g) return { pct:'', net:'' };
  return { pct:String(i/g*100), net:String(Math.round(g+i)) };
}

/* ═══════════════════════════ Main Component ═══════════════════════════════ */
export default function RecommendationsPage() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();
  const authUser    = useSelector(s => s.auth.user);

  // Detect whether we were opened from the standalone /recommendations tab
  // or from inside the Increment Cycles tab (legacy /increment-cycles/:id/hod)
  const fromRecommendations = location.pathname.startsWith('/recommendations');

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [cycle,       setCycle]       = useState(null);
  const [myRole,      setMyRole]      = useState('hod');  // actual logged-in role
  const [role,        setRole]        = useState('hod');  // effective display role
  const [dept,        setDept]        = useState('All');
  const [search,      setSearch]      = useState('');
  const [eligFilter,  setEligFilter]  = useState('All');
  const [mgrFilter,   setMgrFilter]   = useState('All');
  const [locFilter,   setLocFilter]   = useState('All');
  const [submitting,      setSubmitting]      = useState(false);
  const [exporting,       setExporting]       = useState(false);
  const [adminSubmitOpen, setAdminSubmitOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [notesOpen,   setNotesOpen]   = useState(false);
  const [page,        setPage]        = useState(1);
  // View As
  const [viewAs,      setViewAs]      = useState(null);   // null | { id, name, role, department }
  const [viewAsOpen,  setViewAsOpen]  = useState(false);
  const [userOptions, setUserOptions] = useState([]);     // non-admin users for the dropdown
  const isViewAs = viewAs !== null;

  const rowsRef         = useRef(rows);
  const viewAsRef       = useRef(null);   // for click-outside detection
  const adminSubmitRef  = useRef(null);   // for admin submit dropdown click-outside
  const saveTimers      = useRef({});
  useEffect(() => { rowsRef.current = rows; }, [rows]);



  /* ── Sort helper — Eligible (Y) always above Ineligible (N) ──────────── */
  const sortRows = recs => [...recs].sort((a, b) => {
    if (a.increment_pct_eligibility !== b.increment_pct_eligibility)
      return a.increment_pct_eligibility === 'Y' ? -1 : 1;
    return 0;   // preserve server ordering within each group
  });

  /* ── Load ──────────────────────────────────────────────────────────────── */
  useEffect(() => {
    setLoading(true);
    const params = viewAs ? { view_as: viewAs.id } : {};
    Promise.all([cyclesAPI.get(id), cyclesAPI.getHodData(id, params)])
      .then(([cr, hr]) => {
        setCycle(cr.data);
        const serverRole = hr.data.role || authUser?.role || 'hod';
        const serverMyRole = hr.data.my_role || authUser?.role || 'hod';
        setRole(serverRole);
        setMyRole(serverMyRole);
        setRows(sortRows(hr.data.records.map(initRow)));
      })
      .catch(() => toast.error('Failed to load recommendations.'))
      .finally(() => setLoading(false));
  }, [id, authUser, viewAs]);

  /* ── Fetch user list for View As (admin only) ─────────────────────────── */
  useEffect(() => {
    const actualRole = authUser?.role || localStorage.getItem('user_role') || 'admin';
    if (actualRole === 'admin') {
      usersAPI.list()
        .then(r => setUserOptions(
          (r.data || []).filter(u => ['hod', 'hr', 'management'].includes(u.role))
        ))
        .catch(() => {});
    }
  }, [authUser]);

  /* ── Reset to page 1 when any filter or search changes ───────────────────── */
  useEffect(() => { setPage(1); }, [eligFilter, dept, mgrFilter, locFilter, search]);

  /* ── Close View As dropdown on outside click ───────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (viewAsRef.current && !viewAsRef.current.contains(e.target)) setViewAsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Close Admin Submit dropdown on outside click ──────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (adminSubmitRef.current && !adminSubmitRef.current.contains(e.target)) setAdminSubmitOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Save ──────────────────────────────────────────────────────────────── */
  const saveRow = useCallback(async rowId => {
    setRows(prev => prev.map(r => r.id===rowId ? {...r, saving:true, saved:false} : r));
    const row = rowsRef.current.find(r => r.id===rowId);
    if (!row) return;

    const payload = {};
    // When in view-as mode, tell backend which user's role to apply
    if (isViewAs && viewAs) payload.view_as = viewAs.id;

    if (canEditHod(role)) {
      payload.out_of_turn                  = row.out_of_turn;
      payload.hod_recommendation_pct       = row.hod_pct       !== '' ? parseFloat(row.hod_pct)      : null;
      payload.hod_recommendation_increment = row.hod_increment !== '' ? parseInt(row.hod_increment)   : null;
      payload.hod_recommendation_net       = row.hod_net       !== '' ? parseInt(row.hod_net)         : null;
      payload.hod_designation_change       = row.hod_designation_change;
      payload.hod_comments                 = row.hod_comments;
    }
    if (canEditHr(role)) {
      payload.hr_recommendation_pct        = row.hr_pct        !== '' ? parseFloat(row.hr_pct)        : null;
      payload.hr_recommendation_increment  = row.hr_increment  !== '' ? parseInt(row.hr_increment)    : null;
      payload.hr_recommendation_net        = row.hr_net        !== '' ? parseInt(row.hr_net)           : null;
      payload.hr_designation_change        = row.hr_designation_change;
      payload.hr_comments                  = row.hr_comments;
    }
    if (canEditMgmt(role)) {
      payload.mgmt_recommendation_pct       = row.mgmt_pct       !== '' ? parseFloat(row.mgmt_pct)      : null;
      payload.mgmt_recommendation_increment = row.mgmt_increment !== '' ? parseInt(row.mgmt_increment)   : null;
      payload.mgmt_recommendation_net       = row.mgmt_net       !== '' ? parseInt(row.mgmt_net)         : null;
      payload.mgmt_designation_change       = row.mgmt_designation_change;
      payload.mgmt_comments                 = row.mgmt_comments;
    }

    try {
      await cyclesAPI.updateHodData(id, rowId, payload);
      setRows(prev => prev.map(r => r.id===rowId ? {...r, saving:false, saved:true, dirty:false} : r));
      setTimeout(() => setRows(prev => prev.map(r => r.id===rowId ? {...r, saved:false} : r)), 2000);
    } catch {
      toast.error(`Save failed for ${row.emp_id}.`);
      setRows(prev => prev.map(r => r.id===rowId ? {...r, saving:false} : r));
    }
  }, [id, role, isViewAs, viewAs]);

  const schedule = useCallback(rowId => {
    if (saveTimers.current[rowId]) clearTimeout(saveTimers.current[rowId]);
    saveTimers.current[rowId] = setTimeout(() => saveRow(rowId), 500);
  }, [saveRow]);

  /* ── Field change handlers ───────────────────────────────────────────── */
  const onHodPct  = useCallback((rowId, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const { incr, net } = fromPct(val, r.current_grand_gross);
      return { ...r, hod_pct:val, hod_increment:incr, hod_net:net, dirty:true };
    }));
  }, []);

  const onHodIncr = useCallback((rowId, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const { pct, net } = fromIncr(val, r.current_grand_gross);
      return { ...r, hod_increment:val, hod_pct:pct, hod_net:net, dirty:true };
    }));
  }, []);

  const onHrPct   = useCallback((rowId, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const { incr, net } = fromPct(val, r.current_grand_gross);
      return { ...r, hr_pct:val, hr_increment:incr, hr_net:net, dirty:true };
    }));
  }, []);

  const onHrIncr  = useCallback((rowId, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const { pct, net } = fromIncr(val, r.current_grand_gross);
      return { ...r, hr_increment:val, hr_pct:pct, hr_net:net, dirty:true };
    }));
  }, []);

  const onMgPct   = useCallback((rowId, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const { incr, net } = fromPct(val, r.current_grand_gross);
      return { ...r, mgmt_pct:val, mgmt_increment:incr, mgmt_net:net, dirty:true };
    }));
  }, []);

  const onMgIncr  = useCallback((rowId, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const { pct, net } = fromIncr(val, r.current_grand_gross);
      return { ...r, mgmt_increment:val, mgmt_pct:pct, mgmt_net:net, dirty:true };
    }));
  }, []);

  const mkTextHandler = useCallback((rowId, field, val) => {
    setRows(prev => prev.map(r => r.id!==rowId ? r : {...r, [field]:val, dirty:true}));
  }, []);

  // Toggle out_of_turn for N-eligible rows and immediately save
  const onOutOfTurn = useCallback((rowId, checked) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      // When turning off, reset pct/increment to 0 and net to CGG
      const reset = !checked ? {
        hod_pct: '0', hod_increment: '0',
        hod_net: r.current_grand_gross ? String(Math.round(Number(r.current_grand_gross))) : '',
      } : {};
      return { ...r, out_of_turn: checked, ...reset, dirty: true };
    }));
    schedule(rowId);
  }, [schedule]);

  const onBlur = useCallback(rowId => {
    if (rowsRef.current.find(r => r.id===rowId)?.dirty) schedule(rowId);
  }, [schedule]);

  /* ── Submit workflow ─────────────────────────────────────────────────── */
  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const res = await cyclesAPI.downloadConsolidated(id);
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cycle?.cycle_id || id}_consolidated.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [id, cycle]);

  const handleSubmit = useCallback(async () => {
    const effectiveRole = isViewAs ? viewAs?.role : myRole;
    const viewAsId = isViewAs ? viewAs?.id : null;
    const label = effectiveRole === 'hr' ? 'to Management' : 'to HR';
    const who   = isViewAs ? ` on behalf of ${viewAs?.name}` : '';
    if (!window.confirm(`Submit all recommendations ${label}${who}? This cannot be undone.`)) return;
    setSubmitting(true);
    try {
      if (effectiveRole === 'hod' || effectiveRole === 'admin') {
        await cyclesAPI.hodSubmit(id, viewAsId);
      } else if (effectiveRole === 'hr') {
        await cyclesAPI.hrSubmit(id, viewAsId);
      }
      const params = isViewAs ? { view_as: viewAsId } : {};
      const hr = await cyclesAPI.getHodData(id, params);
      setRows(sortRows(hr.data.records.map(initRow)));
      toast.success(effectiveRole === 'hr' ? 'Submitted to Management!' : 'Submitted to HR Head!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }, [id, myRole, isViewAs, viewAs]);

  /* ── Admin: bulk submit on behalf of all HODs / HR / Mgmt ─────────────── */
  const handleAdminSubmitAll = useCallback(async (type) => {
    const hodPendingCount  = rows.filter(r => !r.hod_submitted).length;
    const hrPendingCount   = rows.filter(r => r.hod_submitted && !r.hr_submitted).length;
    const mgmtPendingCount = rows.filter(r => r.hr_submitted  && !r.mgmt_submitted).length;

    const configs = {
      hod:  { label: 'Submit ALL HOD recommendations → HR Head',     count: hodPendingCount  },
      hr:   { label: 'Submit HR Head recommendations → Management',  count: hrPendingCount   },
      mgmt: { label: 'Finalize ALL Management approvals',            count: mgmtPendingCount },
    };
    const { label, count } = configs[type];
    if (!count) { toast('Nothing pending to submit.'); return; }
    if (!window.confirm(`${label}?\n\nThis will submit ${count} record(s). This cannot be undone.`)) return;
    setAdminSubmitOpen(false);
    setSubmitting(true);
    try {
      if (type === 'hod')       await cyclesAPI.hodSubmit(id);
      else if (type === 'hr')   await cyclesAPI.hrSubmit(id);
      else if (type === 'mgmt') await cyclesAPI.mgmtSubmit(id);
      const res = await cyclesAPI.getHodData(id, {});
      setRows(sortRows(res.data.records.map(initRow)));
      toast.success(
        type === 'hod'  ? 'All HOD recommendations submitted to HR!' :
        type === 'hr'   ? 'HR recommendations submitted to Management!' :
                          'Management approvals finalized!'
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }, [id, rows]);

  /* ── Filters / stats ──────────────────────────────────────────────────── */
  const depts = Array.from(new Set(rows.map(r => r.department).filter(Boolean))).sort();
  const mgrs  = Array.from(new Set(rows.map(r => r.reporting_manager).filter(Boolean))).sort();
  const locs  = Array.from(new Set(rows.map(r => r.location).filter(Boolean))).sort();

  const filtered = rows.filter(r => {
    if (eligFilter !== 'All' && r.increment_pct_eligibility !== eligFilter) return false;
    if (dept     !== 'All' && r.department        !== dept)     return false;
    if (mgrFilter !== 'All' && r.reporting_manager !== mgrFilter) return false;
    if (locFilter !== 'All' && r.location          !== locFilter) return false;
    const s = search.toLowerCase();
    return !s || r.emp_id.toLowerCase().includes(s) || r.emp_name.toLowerCase().includes(s);
  });

  const PAGE_SIZE  = 50;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * PAGE_SIZE;
  const paginated  = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const filledKey     = role === 'management' ? 'mgmt_pct' : role === 'hr' ? 'hr_pct' : 'hod_pct';
  const eligibleRows  = rows.filter(r => r.increment_pct_eligibility === 'Y');
  const ineligibleRows= rows.filter(r => r.increment_pct_eligibility === 'N');
  const filledCount   = rows.filter(r => r[filledKey] !== '' && r[filledKey] !== '0').length;

  // Submission state
  const allHodSubmitted  = rows.length > 0 && rows.every(r => r.hod_submitted);
  const allHrSubmitted   = rows.length > 0 && rows.every(r => r.hr_submitted);
  const allMgmtSubmitted = rows.length > 0 && rows.every(r => r.mgmt_submitted);
  const anyHodSubmitted  = rows.some(r => r.hod_submitted);
  const anyHrSubmitted   = rows.some(r => r.hr_submitted);
  const anyMgmtSubmitted = rows.some(r => r.mgmt_submitted);
  // Pending counts for admin submit dropdown
  const hodPending  = rows.filter(r => !r.hod_submitted).length;
  const hrPending   = rows.filter(r => r.hod_submitted && !r.hr_submitted).length;
  const mgmtPending = rows.filter(r => r.hr_submitted  && !r.mgmt_submitted).length;
  const effectiveRole = isViewAs ? (viewAs?.role || 'hod') : myRole;
  // Submit button only appears when actually acting as HOD (direct login OR admin View-As HOD).
  // Admin in default mode has no mass-submit; they must View-As a specific HOD.
  const canSubmitHod = effectiveRole === 'hod' && !allHodSubmitted && rows.length > 0;
  const canSubmitHr  = effectiveRole === 'hr' && anyHodSubmitted && !allHrSubmitted;

  /* ── Loading ──────────────────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:80 }}>
      <span className="spinner" style={{ width:36, height:36 }} />
    </div>
  );

  const pageTitle = role === 'hr' ? 'HR Recommendations' : role === 'management' ? 'Management Approvals' : 'HOD Recommendations';

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    /* Fill the <main> element (which is flex:1 in AppLayout). No page-level scroll. */
    <div style={{ padding:'12px 20px', boxSizing:'border-box' }}>

      {/* Breadcrumb */}
      <div style={{ fontSize:12, color:'var(--grey-400)', marginBottom:8, display:'flex', gap:6, alignItems:'center' }}>
        {fromRecommendations ? (
          <CrumbBtn onClick={() => navigate('/recommendations')}>Recommendations</CrumbBtn>
        ) : (
          <>
            <CrumbBtn onClick={() => navigate('/increment-cycles')}>Increment Cycles</CrumbBtn>
            <span>/</span>
            <CrumbBtn onClick={() => navigate(`/increment-cycles/${id}`)}>{cycle?.cycle_id}</CrumbBtn>
          </>
        )}
        <span>/</span>
        <span style={{ color:'var(--grey-700)', fontWeight:500 }}>{pageTitle}</span>
      </div>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:isViewAs ? 6 : 10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button className="btn-icon magenta"
            onClick={() => fromRecommendations ? navigate('/recommendations') : navigate(`/increment-cycles/${id}`)}
            style={{ width:30, height:30, borderRadius:'50%', background:'var(--magenta)', color:'#fff', flexShrink:0 }}>
            <BackIcon />
          </button>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <h1 style={{ fontSize:18, fontWeight:700 }}>{pageTitle}</h1>
              <RoleBadge role={role} />
            </div>
            <div style={{ fontSize:12, color:'var(--grey-400)', marginTop:1 }}>
              <b style={{ color:'var(--navy)' }}>{cycle?.cycle_id}</b>
              {cycle && <>&ensp;·&ensp;{fmtDate(cycle.start_date)} – {fmtDate(cycle.end_date)}</>}
              {role === 'hod' && viewAs?.department && <>&ensp;·&ensp;Dept: <b style={{ color:'var(--navy)' }}>{viewAs.department}</b></>}
              {role === 'hod' && !viewAs && authUser?.department && <>&ensp;·&ensp;Dept: <b style={{ color:'var(--navy)' }}>{authUser.department}</b></>}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* View As — only for admins */}
          {myRole === 'admin' && (
            <div ref={viewAsRef} style={{ position:'relative' }}>
              <button
                onClick={() => setViewAsOpen(o => !o)}
                style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding:'6px 12px', borderRadius:7, fontSize:12.5, fontWeight:500, cursor:'pointer',
                  border: isViewAs ? '1.5px solid #7C3AED' : '1.5px solid var(--grey-200)',
                  background: isViewAs ? '#F5F3FF' : '#fff',
                  color: isViewAs ? '#7C3AED' : 'var(--grey-600)',
                  whiteSpace:'nowrap',
                }}>
                <span style={{ fontSize:14 }}>👁</span>
                {isViewAs ? `${viewAs.name}` : 'View As'}
                <span style={{ fontSize:10, marginLeft:2 }}>{viewAsOpen ? '▲' : '▼'}</span>
              </button>

              {viewAsOpen && (
                <div style={{
                  position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:100,
                  background:'#fff', border:'1px solid var(--grey-200)', borderRadius:10,
                  boxShadow:'0 8px 24px rgba(0,0,0,0.12)', minWidth:260, maxHeight:380, overflowY:'auto',
                }}>
                  {/* Exit View As */}
                  {isViewAs && (
                    <div
                      onClick={() => { setViewAs(null); setViewAsOpen(false); }}
                      style={{
                        padding:'10px 14px', cursor:'pointer', fontSize:12.5, fontWeight:600,
                        color:'#7C3AED', borderBottom:'1px solid var(--grey-100)',
                        display:'flex', alignItems:'center', gap:8,
                        background:'#F5F3FF',
                      }}>
                      ✕ Exit — Back to Admin View
                    </div>
                  )}
                  {/* My own view option */}
                  {!isViewAs && (
                    <div style={{ padding:'8px 14px 6px', fontSize:11, fontWeight:600,
                      color:'var(--grey-400)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                      Switch to view…
                    </div>
                  )}
                  {/* Grouped options */}
                  {['hod', 'hr', 'management'].map(grpRole => {
                    const grpUsers = userOptions.filter(u => u.role === grpRole);
                    if (!grpUsers.length) return null;
                    const RC = { hod:{bg:'#EFF6FF',c:'#1D4ED8'}, hr:{bg:'#F0FFF4',c:'#15803D'}, management:{bg:'#FFF7ED',c:'#C2410C'} };
                    const rc = RC[grpRole];
                    return (
                      <div key={grpRole}>
                        <div style={{ padding:'6px 14px 4px', fontSize:10.5, fontWeight:700,
                          color:'var(--grey-500)', textTransform:'uppercase', letterSpacing:'0.06em',
                          borderTop:'1px solid var(--grey-100)', background:'#FAFAFA' }}>
                          {grpRole === 'hod' ? 'HOD' : grpRole === 'hr' ? 'HR Head' : 'Management'}
                        </div>
                        {grpUsers.map(u => {
                          const active = viewAs?.id === u.id;
                          return (
                            <div key={u.id}
                              onClick={() => { setViewAs({ id:u.id, name:u.name, role:u.role, department:u.department, emp_id:u.emp_id }); setViewAsOpen(false); }}
                              style={{
                                padding:'9px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:10,
                                background: active ? '#F5F3FF' : 'transparent',
                              }}
                              onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F8FAFC'; }}
                              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                              <div style={{ width:28, height:28, borderRadius:'50%', background:rc.bg,
                                color:rc.c, display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:11, fontWeight:700, flexShrink:0 }}>
                                {u.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontSize:12.5, fontWeight:active?700:500, color:active?'#7C3AED':'var(--grey-900)' }}>
                                  {u.name}{active && ' ✓'}
                                </div>
                                {u.department && (
                                  <div style={{ fontSize:11, color:'var(--grey-400)', marginTop:1 }}>
                                    {u.department}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {userOptions.length === 0 && (
                    <div style={{ padding:'16px 14px', fontSize:12.5, color:'var(--grey-400)' }}>
                      No HOD / HR / Management users yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <input type="text" placeholder="Search ID / Name…" value={search}
            onChange={e => setSearch(e.target.value)} className="form-input"
            style={{ width:190, padding:'6px 11px', fontSize:13 }} />

          {/* Export to Excel — admin / management only */}
          {canSeeMgmt(role) && (
            <button onClick={handleExportExcel} disabled={exporting} style={{
              display:'flex', alignItems:'center', gap:6,
              padding:'6px 13px', borderRadius:7, fontSize:12.5, fontWeight:600,
              cursor: exporting ? 'not-allowed' : 'pointer',
              border:'1.5px solid #166534',
              background: exporting ? '#dcfce7' : '#16a34a', color:'#fff',
              whiteSpace:'nowrap', opacity: exporting ? 0.75 : 1,
            }}>
              {exporting
                ? <><span className="spinner" style={{ width:13, height:13, borderTopColor:'#16a34a', borderColor:'rgba(22,163,74,0.25)' }} /> Exporting…</>
                : <>⬇ Export Excel</>}
            </button>
          )}

          {/* Admin Submit All — admin and management, non view-as mode */}
          {(myRole === 'admin' || myRole === 'management') && !isViewAs && (
            <div ref={adminSubmitRef} style={{ position:'relative' }}>
              <button
                onClick={() => setAdminSubmitOpen(o => !o)}
                disabled={submitting}
                style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding:'6px 13px', borderRadius:7, fontSize:12.5, fontWeight:600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  border:'1.5px solid #4338CA',
                  background: submitting ? '#EEF2FF' : '#4338CA', color: submitting ? '#4338CA' : '#fff',
                  whiteSpace:'nowrap', opacity: submitting ? 0.75 : 1,
                }}>
                {submitting
                  ? <><span className="spinner" style={{ width:13, height:13, borderTopColor:'#4338CA', borderColor:'rgba(67,56,202,0.25)' }} /> Submitting…</>
                  : <>⚡ Admin Submit <span style={{ fontSize:10, marginLeft:2 }}>{adminSubmitOpen ? '▲' : '▼'}</span></>}
              </button>

              {adminSubmitOpen && !submitting && (
                <div style={{
                  position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:200,
                  background:'#fff', border:'1px solid #E2E8F0', borderRadius:12,
                  boxShadow:'0 8px 28px rgba(0,0,0,0.14)', minWidth:310, overflow:'hidden',
                }}>
                  <div style={{ padding:'10px 14px 6px', fontSize:10.5, fontWeight:700,
                    color:'#64748B', textTransform:'uppercase', letterSpacing:'0.06em',
                    background:'#F8FAFC', borderBottom:'1px solid #F1F5F9' }}>
                    Bulk Submit — Admin Override
                  </div>
                  {[
                    {
                      type:'hod', label:'Submit All HODs → HR Head',
                      count: hodPending, done: allHodSubmitted,
                      accent:'#1D4ED8', accentBg:'#EFF6FF',
                      icon:'👥',
                    },
                    {
                      type:'hr', label:'Submit HR Head → Management',
                      count: hrPending, done: allHrSubmitted,
                      accent:'#15803D', accentBg:'#F0FDF4',
                      icon:'🧑‍💼',
                    },
                    {
                      type:'mgmt', label:'Finalize Management Approvals',
                      count: mgmtPending, done: allMgmtSubmitted,
                      accent:'#C2410C', accentBg:'#FFF7ED',
                      icon:'🏢',
                    },
                  ].map((item, idx, arr) => (
                    <button key={item.type}
                      onClick={() => handleAdminSubmitAll(item.type)}
                      disabled={item.count === 0}
                      style={{
                        width:'100%', textAlign:'left', padding:'11px 14px',
                        background: item.count === 0 ? '#FAFAFA' : '#fff',
                        border:'none', borderBottom: idx < arr.length-1 ? '1px solid #F1F5F9' : 'none',
                        cursor: item.count === 0 ? 'not-allowed' : 'pointer',
                        display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
                        opacity: item.count === 0 ? 0.6 : 1,
                      }}
                      onMouseEnter={e => { if (item.count > 0) e.currentTarget.style.background = item.accentBg; }}
                      onMouseLeave={e => { e.currentTarget.style.background = item.count === 0 ? '#FAFAFA' : '#fff'; }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ fontSize:18, lineHeight:1 }}>{item.icon}</span>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color: item.count === 0 ? '#94A3B8' : '#0F172A' }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize:11, marginTop:2,
                            color: item.done ? '#15803D' : item.count > 0 ? item.accent : '#94A3B8' }}>
                            {item.done ? '✓ All submitted' : item.count > 0 ? `${item.count} pending` : 'Nothing to submit'}
                          </div>
                        </div>
                      </div>
                      {item.count > 0 && (
                        <span style={{ fontSize:12, fontWeight:700, color:item.accent,
                          background:item.accentBg, padding:'2px 9px', borderRadius:5,
                          border:`1px solid ${item.accent}33`, flexShrink:0 }}>
                          {item.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Managers' Note */}
          <button onClick={() => setNotesOpen(true)} style={{
            display:'flex', alignItems:'center', gap:6,
            padding:'6px 13px', borderRadius:7, fontSize:12.5, fontWeight:600,
            cursor:'pointer', border:'1.5px solid #047857',
            background:'#047857', color:'#fff', whiteSpace:'nowrap',
          }}>
            📋 Manager's Note
          </button>

          {/* Summary report — management + admin only */}
          {canSeeMgmt(role) && (
            <button onClick={() => setSummaryOpen(true)} style={{
              display:'flex', alignItems:'center', gap:6,
              padding:'6px 13px', borderRadius:7, fontSize:12.5, fontWeight:600,
              cursor:'pointer', border:'1.5px solid var(--navy)',
              background:'var(--navy)', color:'#fff', whiteSpace:'nowrap',
            }}>
              📊 Summary
            </button>
          )}

          {/* Submit button */}
          {canSubmitHod && (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}
              style={{ whiteSpace:'nowrap', gap:6 }}>
              {submitting
                ? <><span className="spinner" style={{ width:13, height:13 }} /> Submitting…</>
                : '✓ Submit to HR'}
            </button>
          )}
          {canSubmitHr && (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}
              style={{ whiteSpace:'nowrap', gap:6 }}>
              {submitting
                ? <><span className="spinner" style={{ width:13, height:13 }} /> Submitting…</>
                : '✓ Submit to Management'}
            </button>
          )}
          {/* Submitted badge */}
          {role === 'hod' && allHodSubmitted && (
            <span style={{ padding:'6px 12px', background:'#DCFCE7', color:'#16A34A',
              border:'1px solid #86EFAC', borderRadius:7, fontSize:12.5, fontWeight:600, whiteSpace:'nowrap' }}>
              ✓ Submitted to HR
            </span>
          )}
          {role === 'hr' && allHrSubmitted && (
            <span style={{ padding:'6px 12px', background:'#DCFCE7', color:'#16A34A',
              border:'1px solid #86EFAC', borderRadius:7, fontSize:12.5, fontWeight:600, whiteSpace:'nowrap' }}>
              ✓ Submitted to Management
            </span>
          )}
        </div>
      </div>

      {/* View As banner */}
      {isViewAs && (
        <div style={{
          display:'flex', alignItems:'center', gap:10,
          background:'#F5F3FF', border:'1.5px solid #DDD6FE', borderRadius:7,
          padding:'6px 14px', marginBottom:8,
        }}>
          <span style={{ fontSize:16 }}>👁</span>
          <div style={{ flex:1 }}>
            <span style={{ fontSize:13, fontWeight:700, color:'#5B21B6' }}>Viewing as {viewAs.name}</span>
            <span style={{ fontSize:12.5, color:'#7C3AED', marginLeft:10 }}>
              <RoleBadge role={viewAs.role} />
            </span>
            {viewAs.department && (
              <span style={{ fontSize:12, color:'#8B5CF6', marginLeft:8 }}>· {viewAs.department}</span>
            )}
          </div>
          <span style={{ fontSize:12, color:'#7C3AED', background:'#EDE9FE', padding:'3px 10px',
            borderRadius:20, fontWeight:500 }}>Editing as this user</span>
          <button onClick={() => setViewAs(null)} style={{
            background:'none', border:'1.5px solid #C4B5FD', borderRadius:6, padding:'4px 10px',
            fontSize:12, color:'#7C3AED', cursor:'pointer', fontWeight:600,
          }}>Exit</button>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8,
        background:'#F8FAFC', border:'1px solid var(--grey-200)', borderRadius:8,
        padding:'7px 12px', flexWrap:'wrap' }}>
        <span style={{ fontSize:12, fontWeight:600, color:'var(--grey-500)', marginRight:2, whiteSpace:'nowrap' }}>Filters:</span>
        <select value={eligFilter} onChange={e => setEligFilter(e.target.value)} style={selStyle(eligFilter !== 'All')}>
          <option value="All">All Eligibility</option>
          <option value="Y">Eligible (Y)</option>
          <option value="N">Ineligible (N)</option>
        </select>
        <select value={dept} onChange={e => setDept(e.target.value)} style={selStyle(dept !== 'All')}>
          <option value="All">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={mgrFilter} onChange={e => setMgrFilter(e.target.value)} style={selStyle(mgrFilter !== 'All')}>
          <option value="All">All Managers</option>
          {mgrs.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={locFilter} onChange={e => setLocFilter(e.target.value)} style={selStyle(locFilter !== 'All')}>
          <option value="All">All Locations</option>
          {locs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {(eligFilter !== 'All' || dept !== 'All' || mgrFilter !== 'All' || locFilter !== 'All') && (
          <button onClick={() => { setEligFilter('All'); setDept('All'); setMgrFilter('All'); setLocFilter('All'); }}
            style={{ padding:'5px 10px', background:'#FEF2F2', border:'1px solid #FCA5A5', borderRadius:6,
              fontSize:12, fontWeight:600, color:'#DC2626', cursor:'pointer', whiteSpace:'nowrap' }}>
            × Clear Filters
          </button>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
          <span style={{ fontSize:11.5, fontWeight:700, color:C.blue, background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:5, padding:'2px 8px' }}>{eligibleRows.length} Eligible</span>
          <span style={{ fontSize:11.5, fontWeight:700, color:'#92400E', background:'#FEF3C7', border:'1px solid #FCD34D', borderRadius:5, padding:'2px 8px' }}>{ineligibleRows.length} Ineligible</span>
          <span style={{ fontSize:11.5, fontWeight:700, color:'#16A34A', background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:5, padding:'2px 8px' }}>{filledCount} Filled</span>
          <span style={{ fontSize:11.5, fontWeight:600, color:'#64748B', background:'#F1F5F9', border:'1px solid #E2E8F0', borderRadius:5, padding:'2px 8px' }}>{filtered.length}/{rows.length}</span>
        </div>
      </div>

      {/* Distribution health checks (from Manager's Note guidelines) */}
      {rows.length > 0 && (
        <DistributionHealth rows={rows} role={role} />
      )}

      {/* Role info strip */}
      <InfoStrip role={role} allHodSubmitted={allHodSubmitted} allHrSubmitted={allHrSubmitted}
        allMgmtSubmitted={allMgmtSubmitted}
        anyHodSubmitted={anyHodSubmitted} anyHrSubmitted={anyHrSubmitted} anyMgmtSubmitted={anyMgmtSubmitted} />

      {/* Table */}
      <div className="card" style={{ padding:0 }}>
        <div style={{ overflow:'auto', maxHeight:'calc(100vh - 360px)' }}>
          <table style={{ borderCollapse:'separate', borderSpacing:0, fontSize:12.5, tableLayout:'fixed', minWidth: canSeeMgmt(role) ? 4940 : canSeeHr(role) ? 4140 : 3340 }}>
            <colgroup>
              {buildCols(role).map((c,i) => <col key={i} style={{ width:c.w }} />)}
            </colgroup>
            <thead style={{ position:'sticky', top:0, zIndex:4 }}>
              <GroupRow role={role} />
              <ColRow   role={role} />
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={buildCols(role).length}
                  style={{ textAlign:'center', padding:'50px 0', color:'var(--grey-400)' }}>
                  {rows.length === 0 ? 'No eligible employees. Generate recommendations (Step 4) first.'
                    : 'No employees match your filters.'}
                </td></tr>
              ) : paginated.map((row, idx) => (
                <DataRow key={row.id} row={row} idx={pageStart + idx} role={role}
                  onHodPct={onHodPct} onHodIncr={onHodIncr}
                  onHrPct={onHrPct}   onHrIncr={onHrIncr}
                  onMgPct={onMgPct}   onMgIncr={onMgIncr}
                  onText={mkTextHandler} onBlur={onBlur} onOutOfTurn={onOutOfTurn}
                  hodLocked={row.hod_submitted && myRole !== 'admin'}
                  hrLocked={row.hr_submitted  && myRole !== 'admin'}
                  mgmtLocked={false} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer — pagination + info */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
        padding:'8px 14px', marginTop:8,
        background:'#fff', border:'1px solid var(--grey-200)', borderRadius:8,
        fontSize:12, color:'var(--grey-400)', flexWrap:'wrap',
      }}>

        {/* Left: row-range info */}
        <div style={{ display:'flex', gap:12, alignItems:'center', flexShrink:0 }}>
          <span>
            <b style={{ color:'var(--grey-700)' }}>
              {filtered.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)}
            </b>
            {' '}of{' '}
            <b style={{ color:'var(--grey-700)' }}>{filtered.length}</b>
            {filtered.length !== rows.length && (
              <span style={{ color:'#C832A0' }}> ({rows.length - filtered.length} filtered)</span>
            )}
          </span>
          <span style={{ color:'var(--grey-200)' }}>|</span>
          <span><b style={{ color:'#047857' }}>{eligibleRows.length}</b> Eligible</span>
          <span style={{ color:'var(--grey-200)' }}>|</span>
          <span><b style={{ color:'#92400E' }}>{ineligibleRows.length}</b> Ineligible</span>
        </div>

        {/* Centre: page controls */}
        {totalPages > 1 && (
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <PagBtn onClick={() => setPage(1)}      disabled={safePage === 1}>«</PagBtn>
            <PagBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>‹</PagBtn>
            {getPageNums(safePage, totalPages).map((p, i) =>
              p === '…'
                ? <span key={`e${i}`} style={{ width:28, textAlign:'center', color:'#94A3B8', fontSize:13 }}>…</span>
                : <PagBtn key={p} active={p === safePage} onClick={() => setPage(p)}>{p}</PagBtn>
            )}
            <PagBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</PagBtn>
            <PagBtn onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</PagBtn>
          </div>
        )}

        {/* Right: meta */}
        <div style={{ display:'flex', gap:12, alignItems:'center', flexShrink:0 }}>
          {totalPages > 1 && (
            <span>Page <b style={{ color:'var(--grey-700)' }}>{safePage}</b> / {totalPages}</span>
          )}
          <span style={{ color:'var(--grey-200)' }}>|</span>
          <span>💡 Auto-saves on blur</span>
          {cycle && (
            <>
              <span style={{ color:'var(--grey-200)' }}>|</span>
              <b style={{ color:'var(--navy)' }}>{cycle.cycle_id}</b>
            </>
          )}
        </div>
      </div>

      {/* Summary modal */}
      {summaryOpen && (
        <SummaryModal rows={rows} cycle={cycle} onClose={() => setSummaryOpen(false)} />
      )}

      {/* Managers' Note modal */}
      {notesOpen && (
        <ManagersNoteModal cycle={cycle} onClose={() => setNotesOpen(false)} />
      )}
    </div>
  );
}

/* ════════════════════════════ Sub-components ══════════════════════════════ */

function RoleBadge({ role }) {
  const colors = { hod:{bg:'#EFF6FF',c:'#1D4ED8'}, hr:{bg:'#F0FFF4',c:'#15803D'},
    management:{bg:'#FFF7ED',c:'#C2410C'}, admin:{bg:'#F3F4F6',c:'#374151'} };
  const s = colors[role] || colors.admin;
  return (
    <span style={{ padding:'3px 10px', borderRadius:6, fontSize:11.5, fontWeight:700,
      background:s.bg, color:s.c, border:`1px solid ${s.c}33` }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function InfoStrip({ role, allHodSubmitted, allHrSubmitted, allMgmtSubmitted, anyHodSubmitted, anyHrSubmitted, anyMgmtSubmitted }) {
  // For HR: show warning if HODs haven't submitted anything yet
  if (role === 'hr' && !anyHodSubmitted) {
    return (
      <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:7,
        padding:'6px 12px', marginBottom:8, fontSize:12.5, color:'#C2410C', display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:16 }}>⏳</span>
        <span><b>Waiting for HOD submissions.</b> HODs must submit their recommendations before HR can proceed.</span>
      </div>
    );
  }
  // For Management: show warning if HR hasn't submitted anything yet
  if (role === 'management' && !anyHrSubmitted) {
    return (
      <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:7,
        padding:'6px 12px', marginBottom:8, fontSize:12.5, color:'#C2410C', display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:16 }}>⏳</span>
        <span><b>Waiting for HR submission.</b> HR Head must submit their recommendations before Management can proceed.</span>
      </div>
    );
  }

  const msgs = {
    hod: allHodSubmitted
      ? '✅ Your recommendations have been submitted to HR. Fields are now locked.'
      : '✏️ Fill in your HOD recommendations. Once ready, click "Submit to HR". HR and Management fields are hidden.',
    hr: allHrSubmitted
      ? '✅ HR recommendations have been submitted to Management. Fields are now locked.'
      : '✏️ HR fields are pre-filled from HOD recommendations. Adjust as needed, then "Submit to Management". Management fields are hidden.',
    management: allMgmtSubmitted
      ? '✅ Management approvals have been finalized.'
      : '✏️ Management fields are pre-filled from HR recommendations. Adjust and save as needed.',
    admin: '✏️ Admin view — all fields visible and editable. Use "Admin Submit" to bulk-submit on behalf of any role.',
  };

  const bg    = (role === 'hod' && allHodSubmitted) || (role === 'hr' && allHrSubmitted) ? '#F0FFF4' : '#EFF6FF';
  const border= (role === 'hod' && allHodSubmitted) || (role === 'hr' && allHrSubmitted) ? '#86EFAC' : '#BFDBFE';
  const color = (role === 'hod' && allHodSubmitted) || (role === 'hr' && allHrSubmitted) ? '#166534' : '#1E40AF';

  return (
    <div style={{ background:bg, border:`1px solid ${border}`, borderRadius:7,
      padding:'6px 12px', marginBottom:8, fontSize:12.5, color, display:'flex', gap:20, flexWrap:'wrap' }}>
      <span>{msgs[role] || ''}</span>
      {!allHodSubmitted && !allHrSubmitted && (
        <span style={{ marginLeft:'auto', color:'#64748B' }}>
          💡 <b>Auto-save</b> on blur · Enter <b>%</b> or <b>Increment</b> — the other auto-fills.
        </span>
      )}
    </div>
  );
}

/* ── Column + group definitions ─────────────────────────────────────────── */
const BASE_COLS = [
  { label:'Employee',     w:240, bg:C.blue, sticky:true },
  // Info
  { label:'Eligibility',  w:75,  bg:C.blue, center:true },
  { label:'Status',       w:80,  bg:C.blue, center:true },
  { label:'Rep. Manager', w:140, bg:C.blue  },
  { label:'DOB',          w:100, bg:C.blue  },
  { label:'Age',          w:55,  bg:C.blue, right:true },
  { label:'DOJ',          w:100, bg:C.blue  },
  { label:'PDS Exp',      w:65,  bg:C.blue, right:true },
  { label:'Total Exp',    w:65,  bg:C.blue, right:true },
  { label:'Variable',     w:65,  bg:C.blue, center:true },
  { label:'Education',    w:120, bg:C.blue  },
  { label:'Institute',    w:150, bg:C.blue  },
  { label:'Degree',       w:120, bg:C.blue  },
  { label:'Last Score',   w:75,  bg:C.blue, right:true },
  { label:'2nd Score',    w:75,  bg:C.blue, right:true },
  { label:'Avg Score',    w:75,  bg:C.blue, right:true },
  { label:'Rank',         w:60,  bg:C.blue, right:true },
  { label:'Eff. Months',  w:70,  bg:C.blue, right:true },
  // Current salary (green)
  { label:'Eff. Date',        w:100, bg:C.green },
  { label:'Curr Grand Gross', w:120, bg:C.green, right:true },
  { label:'Reason',           w:150, bg:C.green },
  // Last (amber)
  { label:'Last Eff. Date',   w:100, bg:C.amber },
  { label:'Last Grand Gross', w:110, bg:C.amber, right:true },
  { label:'Last Reason',      w:150, bg:C.amber },
  // Second last (pink)
  { label:'2L Eff. Date',     w:105, bg:C.pink },
  { label:'2L Grand Gross',   w:110, bg:C.pink, right:true },
  { label:'2L Reason',        w:150, bg:C.pink },
  // Increment %
  { label:'Incr% Scores',     w:85, bg:C.blue, right:true },
  { label:'Incr% Ovr+',       w:75, bg:C.blue, right:true },
  { label:'Incr% Ovr−',       w:75, bg:C.blue, right:true },
  { label:'Incr% Adj',        w:70, bg:C.blue, right:true },
  { label:'Incr% Net',        w:70, bg:C.blue, right:true },
  { label:'Incr% Prorated',   w:85, bg:C.blue, right:true },
  // HOD (orange) — Out of Turn is the first column in this group
  { label:'Out of Turn',      w:82,  bg:C.orange, center:true },
  { label:'HOD %',            w:110, bg:C.orange, right:true, hodEdit:true },
  { label:'HOD Increment',    w:120, bg:C.orange, right:true, hodEdit:true },
  { label:'HOD Net',          w:115, bg:C.orange, right:true, hodNet:true  },
  { label:'HOD Desig.',       w:160, bg:C.orange, hodEdit:true },
  { label:'HOD Comments',     w:380, bg:C.orange, hodEdit:true },
];
const HR_COLS = [
  { label:'HR %',             w:110, bg:C.orange, right:true, hrEdit:true },
  { label:'HR Increment',     w:120, bg:C.orange, right:true, hrEdit:true },
  { label:'HR Net',           w:115, bg:C.orange, right:true, hrNet:true  },
  { label:'HR Desig.',        w:160, bg:C.orange, hrEdit:true },
  { label:'HR Comments',      w:380, bg:C.orange, hrEdit:true },
];
const MGMT_COLS = [
  { label:'Mgmt %',           w:110, bg:C.purple, right:true, mgEdit:true },
  { label:'Mgmt Increment',   w:120, bg:C.purple, right:true, mgEdit:true },
  { label:'Mgmt Net',         w:115, bg:C.purple, right:true, mgNet:true  },
  { label:'Mgmt Desig.',      w:160, bg:C.purple, mgEdit:true },
  { label:'Mgmt Comments',    w:380, bg:C.purple, mgEdit:true },
];
const STATUS_COL = [{ label:'', w:36, bg:'#0F172A' }];

function buildCols(role) {
  if (canSeeMgmt(role)) return [...BASE_COLS, ...HR_COLS, ...MGMT_COLS, ...STATUS_COL];
  if (canSeeHr(role))   return [...BASE_COLS, ...HR_COLS, ...STATUS_COL];
  return [...BASE_COLS, ...STATUS_COL];
}


/* ── Frozen-column helpers ─────────────────────────────────────────────── */
const FROZEN_LEFTS = [0]; // single frozen column at left:0
const FROZEN_COL_COUNT = 1;
const frozenStyle = (bg, ci, extra = {}) => ({
  position: 'sticky',
  left: FROZEN_LEFTS[ci] ?? 0,
  zIndex: 2,
  background: bg,
  boxShadow: ci === 3 ? '4px 0 8px -2px rgba(0,0,0,0.15)' : undefined,
  ...extra,
});

/* ── Group header row ──────────────────────────────────────────────────── */
function GroupRow({ role }) {
  const groups = [
    { label:'Employee & Assessment Info', span:18, color:C.blue   },
    { label:'Current Salary',             span:3,  color:C.green  },
    { label:'Last Change',                span:3,  color:C.amber  },
    { label:'Second Last Change',         span:3,  color:C.pink   },
    { label:'Increment %',                span:6,  color:C.blue   },
    { label:role === 'hod' ? 'HOD Recommendation (enter your recommendation)' : 'HOD Recommendation',
                                          span:6,  color:C.orange },
    ...(canSeeHr(role)   ? [{ label:role==='hr' ? 'HR Recommendation (editable)' : 'HR Recommendation', span:5, color:C.orange }] : []),
    ...(canSeeMgmt(role) ? [{ label:role==='management' ? 'Management Approval (editable)' : 'Management Approval', span:5, color:C.purple }] : []),
    { label:'', span:1, color:'#0F172A' },
  ];

  const thBase = (color, extra = {}) => ({
    background: color, color: 'rgba(255,255,255,0.90)', padding: '5px 10px',
    fontSize: 9.5, fontWeight: 700, textAlign: 'center',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    borderRight: '1px solid rgba(255,255,255,0.12)',
    whiteSpace: 'nowrap', overflow: 'hidden', ...extra,
  });

  const cells = [];
  let cursor = 0;
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    const start = cursor; cursor += g.span;
    const frozenPart    = start < FROZEN_COL_COUNT ? Math.min(FROZEN_COL_COUNT - start, g.span) : 0;
    const nonFrozenPart = g.span - frozenPart;
    if (frozenPart > 0) {
      cells.push(
        <th key={`${i}a`} colSpan={frozenPart} style={thBase(g.color, {
          position: 'sticky', left: 0, zIndex: 8,
          borderRight: nonFrozenPart > 0 ? '1px solid rgba(255,255,255,0.15)' : '2px solid rgba(255,255,255,0.25)',
        })}>{nonFrozenPart === 0 ? g.label : ''}</th>
      );
    }
    if (nonFrozenPart > 0) {
      cells.push(<th key={`${i}b`} colSpan={nonFrozenPart} style={thBase(g.color)}>{g.label}</th>);
    }
  }
  return <tr>{cells}</tr>;
}

/* ── Column header row ─────────────────────────────────────────────────── */
function ColRow({ role }) {
  const cols = buildCols(role);
  return (
    <tr>{cols.map((c, ci) => (
      <th key={ci} style={{
        background: c.bg, color: 'rgba(255,255,255,0.92)', padding: '9px 10px',
        fontSize: 11, fontWeight: 600, lineHeight: 1.3, letterSpacing: '0.01em',
        textAlign: 'center',
        borderRight: '1px solid rgba(255,255,255,0.10)',
        borderBottom: '3px solid rgba(0,0,0,0.20)',
        whiteSpace: 'normal', filter: 'brightness(0.82)',
        ...(c.sticky ? frozenStyle(c.bg, ci, { zIndex: 5 }) : {}),
      }}>{c.label}</th>
    ))}</tr>
  );
}

/* ── Data row ──────────────────────────────────────────────────────────── */
function DataRow({ row, idx, role, onHodPct, onHodIncr, onHrPct, onHrIncr, onMgPct, onMgIncr, onText, onBlur, onOutOfTurn, hodLocked, hrLocked, mgmtLocked }) {
  const ineligible = row.increment_pct_eligibility === 'N';
  // N-eligible rows get a subtle warm tint so they're visually distinct
  const bg = ineligible ? (idx%2===0 ? '#FFFDF4' : '#FFF8E0') : (idx%2===0 ? '#FFFFFF' : '#F8FAFC');
  const id = row.id;

  // Helpers
  const ro = (val, opts={}) => (
    <td style={{ padding:'8px 10px', background:opts.bg||bg, textAlign:opts.right?'right':'left',
      fontFamily:opts.mono?'"SF Mono","Fira Code","Cascadia Code",monospace':'inherit',
      fontWeight:opts.bold?600:400, color:opts.color||'#1E293B',
      borderBottom:'1px solid #F1F5F9', borderRight:'1px solid #F1F5F9',
      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:13 }}>
      {val ?? '—'}
    </td>
  );

  // Editable group renderer
  const editGroup = (prefix, pctVal, incrVal, netVal, desigVal, commentsVal,
                     canEdit, onPct, onIncr, accentBg, accentBorder, netBg, netBorderColor) => {
    if (canEdit) {
      return (<>
        {/* % */}
        <td style={{ padding:'4px 6px', background:accentBg, borderBottom:`1px solid ${accentBorder}`, borderRight:`1px solid ${accentBorder}` }}>
          <div className="rec-input-group" style={{ display:'flex', alignItems:'center', background:'#fff', border:`1.5px solid ${accentBorder}`, borderRadius:8, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
            <input type="number" step="0.0001" min="0" max="100" placeholder="—"
              value={pctVal} onChange={e => onPct(id, e.target.value)} onBlur={() => onBlur(id)}
              className="no-spin"
              style={{ flex:1, padding:'5px 8px', border:'none', outline:'none', fontSize:13, textAlign:'right', background:'transparent', color:'#0F172A', fontWeight:600, fontFamily:'"SF Mono","Cascadia Code",monospace', minWidth:0 }} />
            <span style={{ padding:'0 8px 0 2px', fontSize:11, color:'#94A3B8', fontWeight:700, flexShrink:0, userSelect:'none' }}>%</span>
          </div>
        </td>
        {/* Increment */}
        <td style={{ padding:'4px 6px', background:accentBg, borderBottom:`1px solid ${accentBorder}`, borderRight:`1px solid ${accentBorder}` }}>
          <div className="rec-input-group" style={{ display:'flex', alignItems:'center', background:'#fff', border:`1.5px solid ${accentBorder}`, borderRadius:8, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
            <span style={{ padding:'0 4px 0 8px', fontSize:11, color:'#94A3B8', fontWeight:700, flexShrink:0, userSelect:'none' }}>₹</span>
            <input type="number" step="1" placeholder="—"
              value={incrVal} onChange={e => onIncr(id, e.target.value)} onBlur={() => onBlur(id)}
              className="no-spin"
              style={{ flex:1, padding:'5px 8px', border:'none', outline:'none', fontSize:13, textAlign:'right', background:'transparent', color:'#0F172A', fontWeight:600, fontFamily:'"SF Mono","Cascadia Code",monospace', minWidth:0 }} />
          </div>
        </td>
        {/* Net — read-only auto-computed */}
        <td style={{ padding:'4px 8px', textAlign:'right', background:netBg,
          borderBottom:`1px solid ${netBorderColor}`, borderRight:`1px solid ${netBorderColor}` }}>
          <span style={{ fontFamily:'"SF Mono","Cascadia Code",monospace', fontSize:13, fontWeight:700, color:'#1E3A5F' }}>
            {netVal !== '' ? fmtCurr(netVal) : <span style={{ color:netBorderColor }}>—</span>}
          </span>
        </td>
        {/* Designation Change */}
        <td style={{ padding:'4px 6px', background:accentBg, borderBottom:`1px solid ${accentBorder}`, borderRight:`1px solid ${accentBorder}` }}>
          <input type="text" placeholder="—"
            value={desigVal} onChange={e => onText(id, `${prefix}_designation_change`, e.target.value)}
            onBlur={() => onBlur(id)} className="rec-text-input" style={txtIn(accentBorder)} />
        </td>
        {/* Comments */}
        <td style={{ padding:'4px 6px', background:accentBg, borderBottom:`1px solid ${accentBorder}`, borderRight:`1px solid ${accentBorder}`, height:'1px' }}>
          <textarea placeholder="—"
            value={commentsVal} onChange={e => onText(id, `${prefix}_comments`, e.target.value)}
            onBlur={() => onBlur(id)} className="rec-text-input" style={taIn(accentBorder)} />
        </td>
      </>);
    }
    // Read-only
    return (<>
      {ro((pctVal !== '' ? fmtPctDisplay(pctVal) : '—'), { right:true, bg:accentBg })}
      {ro((incrVal !== '' ? fmtCurr(incrVal) : '—'), { right:true, mono:true, bg:accentBg })}
      {ro((netVal  !== '' ? fmtCurr(netVal)  : '—'), { right:true, mono:true, bold:true, bg:netBg })}
      {ro(desigVal  || '—', { bg:accentBg })}
      {ro(commentsVal || '—', { bg:accentBg })}
    </>);
  };

  return (
    <tr>
      <td style={{ padding:'8px 10px', ...frozenStyle(bg, 0), borderBottom:'1px solid #F1F5F9', borderRight:'2px solid #E2E8F0', maxWidth:240 }}>
        <div style={{ fontWeight:700, fontSize:13.5, color:'#0F172A', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{row.emp_name ?? '—'}</div>
        <div style={{ fontSize:12, fontWeight:600, color:'#1E293B', marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{row.designation ?? '—'}</div>
        <div style={{ fontSize:11, fontWeight:500, color:'#475569', marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {[row.location, row.department, row.stream].filter(Boolean).join(' · ')}
        </div>
        <div style={{ fontSize:12, fontWeight:500, color:'#64748B', marginTop:2, fontFamily:'"SF Mono","Cascadia Code",monospace', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {row.emp_id}
        </div>
      </td>
      {/* Eligibility badge */}
      <td style={{ padding:'6px 10px', textAlign:'center', background:bg, borderBottom:'1px solid #F1F5F9', borderRight:'1px solid #F1F5F9' }}>
        <span title={ineligible ? 'Not eligible — HOD can still override with an increment' : 'Eligible for increment'}
          style={{ padding:'2px 8px', borderRadius:5, fontSize:11, fontWeight:700,
            ...(row.increment_pct_eligibility==='Y'
              ? { background:'#D1FAE5', color:'#064E3B', border:'1px solid #6EE7B7' }
              : { background:'#FEF9C3', color:'#78350F', border:'1px solid #FDE047' }) }}>
          {row.increment_pct_eligibility || '—'}
        </span>
      </td>
      {/* Status */}
      <td style={{ padding:'6px 10px', textAlign:'center', borderBottom:'1px solid #F1F5F9',
        borderRight:'1px solid #F1F5F9',
        background: row.employment_status==='Active' ? '#F0FDF4' : bg }}>
        <span style={{ fontSize:11.5, fontWeight:500, color:row.employment_status==='Active'?'#047857':'#64748B' }}>
          {row.employment_status || '—'}
        </span>
      </td>
      {ro(row.reporting_manager)}
      {ro(fmtDate(row.dob))}
      {ro(fmtNum(row.age, 1),              { right:true })}
      {ro(fmtDate(row.doj))}
      {ro(fmtNum(row.pds_exp, 1),          { right:true })}
      {ro(fmtNum(row.total_exp_years, 1),  { right:true })}
      {/* Variable */}
      <td style={{ padding:'6px 10px', textAlign:'center', borderBottom:'1px solid #F1F5F9',
        borderRight:'1px solid #F1F5F9',
        background: row.variable==='Fix'?'#DCFCE7':row.variable==='PFP'?'#FEE2E2':bg }}>
        <span style={{ fontSize:11.5, fontWeight:500,
          color: row.variable==='Fix'?'#065F46':row.variable==='PFP'?'#991B1B':'#475569' }}>
          {row.variable||'—'}
        </span>
      </td>
      {ro(row.level_of_education)}
      {ro(row.educational_institute)}
      {ro(row.degree)}
      {ro(fmtNum(row.last_assessment_score, 2),        { right:true })}
      {ro(fmtNum(row.second_last_assessment_score, 2), { right:true })}
      {ro(fmtNum(row.avg_assessment_score, 4),         { right:true })}
      {ro(fmtRank(row.rank),                           { right:true })}
      {ro(fmtNum(row.effective_months, 1),             { right:true })}

      {/* Current salary (green bg) */}
      {ro(fmtDate(row.effective_date),         { bg:'#F0FDF4' })}
      {ro(fmtCurr(row.current_grand_gross),    { right:true, mono:true, bold:true, bg:'#F0FDF4', color:'#065F46' })}
      {ro(row.reason_for_change||'—',          { bg:'#F0FDF4' })}

      {/* Last change (amber bg) */}
      {ro(fmtDate(row.last_change_effective_date), { bg:'#FFFBEB' })}
      {ro(fmtCurr(row.last_grand_gross),           { right:true, mono:true, bg:'#FFFBEB' })}
      {ro(row.last_reason_for_change||'—',         { bg:'#FFFBEB' })}

      {/* Second last (pink bg) */}
      {ro(fmtDate(row.second_last_change_effective_date), { bg:'#FDF2F8' })}
      {ro(fmtCurr(row.second_last_grand_gross),           { right:true, mono:true, bg:'#FDF2F8' })}
      {ro(row.second_last_reason_for_change||'—',         { bg:'#FDF2F8' })}

      {/* Increment % */}
      {ro(fmtPctFrac(row.increment_pct_from_scores),     { right:true })}
      {ro(fmtPctFrac(row.increment_pct_overriding_plus), { right:true })}
      {ro(fmtPctFrac(row.increment_pct_overriding_minus),{ right:true })}
      {ro(fmtPctFrac(row.increment_pct_adjustment),      { right:true })}
      {ro(fmtPctFrac(row.increment_pct_net),             { right:true })}
      {ro(fmtPctFrac(row.increment_pct_prorated),        { right:true })}

      {/* Out of Turn — first column of the HOD Recommendation group */}
      <td style={{ padding:'5px 10px', textAlign:'center',
        background: ineligible ? (row.out_of_turn ? '#FFF3E0' : '#FFFBF0') : '#FFFBF0',
        borderBottom:'1px solid #FDE8C8', borderRight:'1px solid #FDE8C8' }}>
        {ineligible ? (
          <label title={row.out_of_turn ? 'Out-of-turn increment enabled — HOD fields are editable' : 'Tick to allow an out-of-turn increment for this employee'}
            style={{ display:'inline-flex', alignItems:'center', gap:5, cursor: canEditHod(role) && !hodLocked ? 'pointer' : 'default' }}>
            <input
              type="checkbox"
              checked={row.out_of_turn}
              disabled={!canEditHod(role) || hodLocked}
              onChange={e => onOutOfTurn(row.id, e.target.checked)}
              style={{ width:14, height:14, accentColor:'#C2410C', cursor:'inherit' }}
            />
            {row.out_of_turn && <span style={{ fontSize:10, fontWeight:700, color:'#C2410C' }}>OOT</span>}
          </label>
        ) : <span style={{ color:'#FDE8C8', fontSize:11 }}>—</span>}
      </td>

      {/* HOD group — locked after HOD submits OR if ineligible without out_of_turn */}
      {(() => {
        const ootBlocked = ineligible && !row.out_of_turn;  // grayed — not an OOT increment
        const locked     = hodLocked || ootBlocked;
        return editGroup('hod', row.hod_pct, row.hod_increment, row.hod_net,
          row.hod_designation_change, row.hod_comments,
          canEditHod(role) && !locked, onHodPct, onHodIncr,
          locked ? '#F8FAFC' : '#FFFBF0',
          locked ? '#E2E8F0' : '#FDE8C8',
          locked ? '#F1F5F9' : '#FEF9C3',
          locked ? '#CBD5E1' : '#FCD34D');
      })()}

      {/* HR group */}
      {canSeeHr(role) && (() => {
        const ootBlocked = ineligible && !row.out_of_turn;
        const locked     = hrLocked || ootBlocked;
        return editGroup('hr', row.hr_pct, row.hr_increment, row.hr_net,
          row.hr_designation_change, row.hr_comments,
          canEditHr(role) && !locked, onHrPct, onHrIncr,
          locked ? '#F8FAFC' : '#FFFBF0',
          locked ? '#E2E8F0' : '#FDE8C8',
          locked ? '#F1F5F9' : '#FEF9C3',
          locked ? '#CBD5E1' : '#FCD34D');
      })()}

      {/* Mgmt group */}
      {canSeeMgmt(role) && (() => {
        const ootBlocked = ineligible && !row.out_of_turn;
        const locked     = mgmtLocked || ootBlocked;
        return editGroup('mgmt', row.mgmt_pct, row.mgmt_increment, row.mgmt_net,
          row.mgmt_designation_change, row.mgmt_comments,
          canEditMgmt(role) && !locked, onMgPct, onMgIncr,
          locked ? '#F8FAFC' : '#FAF5FF',
          locked ? '#E2E8F0' : '#E9D5FF',
          locked ? '#F1F5F9' : '#F3E8FF',
          locked ? '#CBD5E1' : '#D8B4FE');
      })()}

      {/* Save status / lock indicator */}
      <td style={{ padding:'5px', textAlign:'center', background:bg, borderBottom:'1px solid #F1F5F9', width:36 }}>
        {row.saving ? <span className="spinner" style={{ width:13, height:13 }} />
          : row.saved  ? <span style={{ color:'#16A34A', display:'inline-flex' }}><CheckIcon /></span>
          : row.dirty  ? <span title="Unsaved" style={{ width:8, height:8, borderRadius:'50%',
              background:'#F59E0B', display:'inline-block' }} />
          : (hodLocked || hrLocked)
            ? <span title="Submitted — locked" style={{ fontSize:12, color:'#94A3B8' }}>🔒</span>
          : null}
      </td>
    </tr>
  );
}

/* ── Tiny helpers ──────────────────────────────────────────────────────── */
function fmtPctDisplay(v) {   // user-entered pct (stored as actual % value e.g. 8.5)
  if (v == null || v === '') return '—';
  const n = Number(v); return isNaN(n) ? '—' : n.toFixed(4).replace(/\.?0+$/, '') + '%';
}


function CrumbBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{ background:'none', border:'none', cursor:'pointer',
      color:'var(--grey-400)', padding:0, fontSize:12.5 }}>
      {children}
    </button>
  );
}

/* ── Pagination helpers ─────────────────────────────────────────────────── */
function PagBtn({ children, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: 30, height: 30, borderRadius: 6, fontSize: 12.5, fontWeight: active ? 700 : 500,
      border: active ? 'none' : '1.5px solid var(--grey-200)',
      background: active ? 'var(--navy)' : 'transparent',
      color: active ? '#fff' : disabled ? '#D1D5DB' : '#374151',
      cursor: disabled ? 'default' : 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.12s',
    }}>{children}</button>
  );
}

function getPageNums(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total-4, total-3, total-2, total-1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}


const txtIn = border => ({
  width:'100%', padding:'5px 9px', border:`1.5px solid ${border}`,
  borderRadius:8, fontSize:12.5,
  background:'#fff', outline:'none', color:'#0F172A',
  boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
});
// Textarea variant for comments — vertically resizable, no character limit
const taIn = border => ({
  width:'100%', height:'100%', padding:'5px 9px', border:`1.5px solid ${border}`,
  borderRadius:8, fontSize:12.5, lineHeight:1.5, fontFamily:'inherit',
  background:'#fff', outline:'none', color:'#0F172A',
  boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
  resize:'vertical', minHeight:60, display:'block', boxSizing:'border-box',
});
// Filter bar select styling — highlights when a filter is active
const selStyle = active => ({
  padding:'5px 10px', border:`1.5px solid ${active ? '#C832A0' : '#E5E7EB'}`,
  borderRadius:6, fontSize:12.5, cursor:'pointer', outline:'none',
  color: active ? '#C832A0' : '#374151',
  background: active ? 'rgba(200,50,160,0.04)' : '#fff',
  fontWeight: active ? 600 : 400,
});

/* ════════════════════════ Summary Report Modal ════════════════════════════ */
function SummaryModal({ rows, cycle, onClose }) {
  const [hovered, setHovered] = React.useState(null);

  // Group all rows by department
  const byDept = {};
  rows.forEach(row => {
    const d = row.department || '—';
    if (!byDept[d]) byDept[d] = { current: 0, approved: 0, count: 0 };
    const curr = Number(row.current_grand_gross) || 0;
    const appr = row.mgmt_net !== '' && row.mgmt_net != null ? Number(row.mgmt_net) : curr;
    byDept[d].current  += curr;
    byDept[d].approved += appr;
    byDept[d].count    += 1;
  });

  const deptRows = Object.entries(byDept)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dept, d]) => ({
      dept, current: d.current, approved: d.approved, count: d.count,
      pct: d.current > 0 ? (d.approved - d.current) / d.current * 100 : 0,
    }));

  const totCurrent  = deptRows.reduce((s, r) => s + r.current,  0);
  const totApproved = deptRows.reduce((s, r) => s + r.approved, 0);
  const totPct      = totCurrent > 0 ? (totApproved - totCurrent) / totCurrent * 100 : 0;

  const fmtINR = n => '₹' + Math.round(n).toLocaleString('en-IN');
  const mono = '"SF Mono","Cascadia Code","Fira Code",monospace';

  const pctBadge = p => {
    if (p > 18) return { bg:'#D1FAE5', color:'#065F46', border:'#6EE7B7' };
    if (p > 13) return { bg:'#DCFCE7', color:'#15803D', border:'#86EFAC' };
    if (p > 9)  return { bg:'#DBEAFE', color:'#1D4ED8', border:'#93C5FD' };
    if (p > 5)  return { bg:'#FEF3C7', color:'#92400E', border:'#FCD34D' };
    if (p > 0)  return { bg:'#F1F5F9', color:'#475569', border:'#CBD5E1' };
    return               { bg:'#FEE2E2', color:'#991B1B', border:'#FCA5A5' };
  };

  const TH = ({ children, center, w }) => (
    <th style={{
      padding: '13px 20px', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
      textTransform: 'uppercase', color: '#64748B', whiteSpace: 'nowrap',
      textAlign: center ? 'center' : 'left', background: '#F1F5F9',
      borderBottom: '2px solid #E2E8F0', width: w || undefined,
    }}>{children}</th>
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(10,15,35,0.72)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 18, width: '100%', maxWidth: 920,
        maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 32px 80px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.06)',
      }}>

        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1A2744 0%, #0F172A 100%)',
          padding: '22px 28px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{
              fontSize: 19, fontWeight: 800, color: '#fff',
              letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{
                width: 36, height: 36, background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 10, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18,
              }}>📊</span>
              Increment Summary Report
            </div>
            {cycle && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.40)', marginTop: 6, letterSpacing: '0.02em' }}>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>{cycle.cycle_id}</span>
                {cycle.start_date && (
                  <span> &nbsp;·&nbsp; {fmtDate(cycle.start_date)} – {fmtDate(cycle.end_date)}</span>
                )}
                <span> &nbsp;·&nbsp; </span>
                <span style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {rows.length} employees &nbsp;·&nbsp; {deptRows.length} departments
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 10, padding: '8px 18px',
            color: 'rgba(255,255,255,0.75)',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, letterSpacing: '0.02em',
          }}>✕ &nbsp;Close</button>
        </div>

        {/* ── Table ── */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <TH>Department</TH>
                <TH center w={110}>Employees</TH>
                <TH center>Current Grand Gross</TH>
                <TH center>Approved Grand Gross</TH>
                <TH center w={130}>% Increase</TH>
              </tr>
            </thead>
            <tbody>
              {deptRows.map((r, i) => {
                const badge = pctBadge(r.pct);
                const isHov = hovered === i;
                return (
                  <tr key={r.dept}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ background: isHov ? '#EFF6FF' : i % 2 === 0 ? '#fff' : '#FAFBFC',
                      borderBottom: '1px solid #F1F5F9', transition: 'background 0.12s' }}>

                    {/* Department pill */}
                    <td style={{ padding: '12px 20px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        background: isHov ? '#DBEAFE' : '#EFF6FF',
                        color: '#1D4ED8', border: '1px solid #BFDBFE',
                        padding: '4px 12px', borderRadius: 7,
                        fontSize: 12.5, fontWeight: 700, letterSpacing: '0.04em',
                        transition: 'background 0.12s',
                      }}>{r.dept}</span>
                    </td>

                    {/* Employees */}
                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>{r.count}</span>
                    </td>

                    {/* Current Grand Gross */}
                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                      <span style={{ fontFamily: mono, fontSize: 13.5, color: '#475569', fontWeight: 500 }}>
                        {fmtINR(r.current)}
                      </span>
                    </td>

                    {/* Approved Grand Gross */}
                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                      <span style={{ fontFamily: mono, fontSize: 13.5, color: '#0F172A', fontWeight: 700 }}>
                        {fmtINR(r.approved)}
                      </span>
                    </td>

                    {/* % Increase badge */}
                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 14px', borderRadius: 20,
                        fontSize: 13, fontWeight: 700,
                        color: badge.color, background: badge.bg,
                        border: `1px solid ${badge.border}`,
                      }}>{r.pct.toFixed(2)}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* ── Grand Total ── */}
            <tfoot>
              <tr style={{ background: 'linear-gradient(90deg,#1A2744,#0F172A)' }}>
                <td style={{ padding: '15px 20px', fontWeight: 800, fontSize: 13, color: '#fff',
                  letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Grand Total
                </td>
                <td style={{ padding: '15px 20px', textAlign: 'center', fontSize: 17, fontWeight: 800, color: '#fff' }}>
                  {rows.length}
                </td>
                <td style={{ padding: '15px 20px', textAlign: 'center' }}>
                  <span style={{ fontFamily: mono, fontSize: 13.5, color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>
                    {fmtINR(totCurrent)}
                  </span>
                </td>
                <td style={{ padding: '15px 20px', textAlign: 'center' }}>
                  <span style={{ fontFamily: mono, fontSize: 13.5, color: '#fff', fontWeight: 800 }}>
                    {fmtINR(totApproved)}
                  </span>
                </td>
                <td style={{ padding: '15px 20px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '5px 16px', borderRadius: 20,
                    fontSize: 14, fontWeight: 800, color: '#fff',
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.28)',
                  }}>{totPct.toFixed(2)}%</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: '9px 24px', borderTop: '1px solid #E5E7EB',
          fontSize: 11.5, color: '#94A3B8', background: '#F8FAFC' }}>
          * Approved values cascade: saved Management → HR → HOD → Prorated %. All employees included.
        </div>
      </div>
    </div>
  );
}

/* ════════════════ Distribution Health (Manager's Note checks) ════════════ */
/*
 * Checks derived from the Manager's Note guidelines:
 *  §5  Bucket A ≤ 15% of eligible team
 *  §3  Average increment target ~8%
 *  §9  Meaningful representation in low / zero bucket (≥ 10%)
 *  §9  No clustering — >50% in a single 2% band is flagged
 *  §5  B vs C gap — avg(B) should be at least 2pp above avg(C)
 */
function DistributionHealth({ rows, role }) {
  const [expanded, setExpanded] = React.useState(false);
  const pctField =
    role === 'management' || role === 'admin' ? 'mgmt_pct'
    : role === 'hr' ? 'hr_pct'
    : 'hod_pct';

  // Eligible employees (Y + any OOT ineligible that do get an increment)
  const elig = rows.filter(r => r.increment_pct_eligibility === 'Y' || r.out_of_turn);
  const n = elig.length;
  if (n === 0) return null;

  const pv = r => { const v = r[pctField]; return (v == null || v === '') ? 0 : Number(v); };

  // Buckets (§5)
  const bA = elig.filter(r => pv(r) >= 12);
  const bB = elig.filter(r => pv(r) >= 8  && pv(r) < 12);
  const bC = elig.filter(r => pv(r) >= 4  && pv(r) < 8);
  const bD = elig.filter(r => pv(r) >= 0  && pv(r) < 4);

  const pA = bA.length / n * 100;
  const pB = bB.length / n * 100;
  const pC = bC.length / n * 100;
  const pD = bD.length / n * 100;

  // Average increment (§3)
  const avg = elig.reduce((s, r) => s + pv(r), 0) / n;

  // B vs C differentiation (§9)
  const avgB = bB.length > 0 ? bB.reduce((s,r) => s + pv(r), 0) / bB.length : 0;
  const avgC = bC.length > 0 ? bC.reduce((s,r) => s + pv(r), 0) / bC.length : 0;
  const bcGap = avgB - avgC;

  // Clustering: any 2% band with > 50% of employees (§9)
  const bands = {};
  elig.forEach(r => { const k = Math.floor(pv(r) / 2) * 2; bands[k] = (bands[k] || 0) + 1; });
  const maxBandPct = Math.max(...Object.values(bands)) / n * 100;
  const maxBandVal = Object.entries(bands).find(([,v]) => v / n * 100 === maxBandPct);

  // ── Health helpers ────────────────────────────────────────────────────────
  const OK   = 'ok';
  const WARN = 'warn';
  const BAD  = 'bad';

  const hcol  = h => h === OK ? '#059669' : h === WARN ? '#B45309' : '#DC2626';
  const hbg   = h => h === OK ? '#F0FDF4' : h === WARN ? '#FFFBEB' : '#FFF5F5';
  const hbdr  = h => h === OK ? '#86EFAC' : h === WARN ? '#FDE68A' : '#FCA5A5';
  const hicon = h => h === OK ? '✓' : h === WARN ? '⚠' : '✗';

  const hAvg = avg >= 7 && avg <= 9     ? OK : avg >= 6 && avg <= 10   ? WARN : BAD;
  const hA   = pA <= 15                 ? OK : pA <= 20                ? WARN : BAD;
  const hD   = pD >= 10                 ? OK : pD >= 5                 ? WARN : BAD;
  const hBC  = bcGap >= 2               ? OK : bcGap >= 0.5            ? WARN : BAD;
  const hCluster = maxBandPct <= 40     ? OK : maxBandPct <= 55        ? WARN : BAD;

  const overallOk = [hAvg, hA, hD, hBC, hCluster].every(h => h === OK);
  const hasAnyBad = [hAvg, hA, hD, hBC, hCluster].some(h => h === BAD);

  // ── Checks array (only surfaced when not OK) ──────────────────────────────
  const checks = [
    {
      h: hAvg,
      label: 'Avg Increment',
      value: `${avg.toFixed(1)}%`,
      note: hAvg === OK ? 'On target' : `Target ~8% (currently ${avg.toFixed(1)}%)`,
    },
    {
      h: hA,
      label: 'Bucket A',
      value: `${bA.length} (${pA.toFixed(0)}%)`,
      note: hA === OK ? '≤ 15% ✓' : `Guideline ≤ 15% — currently ${pA.toFixed(0)}%`,
    },
    {
      h: hD,
      label: 'Low / Zero',
      value: `${bD.length} (${pD.toFixed(0)}%)`,
      note: hD === OK ? '≥ 10% ✓' : `Needs ≥ 10% — currently ${pD.toFixed(0)}%`,
    },
    {
      h: hBC,
      label: 'B vs C Gap',
      value: `${bcGap.toFixed(1)}pp`,
      note: hBC === OK ? 'Well differentiated' : `Gap ${bcGap.toFixed(1)}pp — target ≥ 2pp`,
    },
    {
      h: hCluster,
      label: 'Clustering',
      value: `${maxBandPct.toFixed(0)}% in ${maxBandVal ? maxBandVal[0] : '?'}–${maxBandVal ? (Number(maxBandVal[0])+2) : '?'}%`,
      note: hCluster === OK ? 'No clustering' : `${maxBandPct.toFixed(0)}% of team in one 2% band`,
    },
  ];

  const borderColor = overallOk ? '#86EFAC' : hasAnyBad ? '#FCA5A5' : '#FDE68A';
  const headerBg    = overallOk ? '#F0FDF4' : hasAnyBad ? '#FFF5F5' : '#FFFBEB';
  const headerColor = overallOk ? '#065F46' : hasAnyBad ? '#7F1D1D' : '#78350F';

  return (
    <div style={{
      marginBottom: 8, border: `1.5px solid ${borderColor}`, borderRadius: 10,
      background: '#fff', overflow: 'hidden',
    }}>
      {/* Header bar — click to expand/collapse */}
      <div onClick={() => setExpanded(e => !e)} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '7px 14px', background: headerBg, cursor: 'pointer', userSelect: 'none',
        ...(expanded ? { borderBottom: `1px solid ${borderColor}` } : {}),
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12 }}>📐</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: headerColor,
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Distribution Health
          </span>
          <span style={{ fontSize: 11, color: headerColor, opacity: 0.65 }}>
            · {n} eligible
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: headerColor }}>
            {overallOk ? '✓ All checks passed'
              : hasAnyBad ? '✗ Issues found'
              : '⚠ Caution'}
          </span>
          {!expanded && (
            <div style={{ display:'flex', gap:3 }}>
              {[hAvg, hA, hD, hBC, hCluster].map((h, i) => (
                <span key={i} style={{
                  width:7, height:7, borderRadius:'50%', display:'inline-block',
                  background: h===OK ? '#059669' : h===WARN ? '#D97706' : '#DC2626',
                }} />
              ))}
            </div>
          )}
          <span style={{ fontSize:10, color:headerColor, opacity:0.5 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Check pills — only when expanded */}
      {expanded && <div style={{ display: 'flex', gap: 8, padding: '10px 14px', flexWrap: 'wrap', alignItems: 'center' }}>
        {checks.map(c => (
          <div key={c.label} style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '5px 12px', borderRadius: 8,
            background: hbg(c.h), border: `1px solid ${hbdr(c.h)}`,
            cursor: 'default', position: 'relative',
          }} title={c.note}>
            <span style={{ fontSize: 11, fontWeight: 800, color: hcol(c.h) }}>{hicon(c.h)}</span>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: hcol(c.h),
                textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.2 }}>
                {c.label}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: hcol(c.h), lineHeight: 1.2 }}>
                {c.value}
              </div>
            </div>
          </div>
        ))}

        {/* Mini bucket bar */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 10.5, color: '#94A3B8', marginRight: 4 }}>Distribution:</span>
          {[
            { l:'A', p:pA, c:'#059669' },
            { l:'B', p:pB, c:'#2563EB' },
            { l:'C', p:pC, c:'#D97706' },
            { l:'D', p:pD, c:'#DC2626' },
          ].map(b => (
            <div key={b.l} title={`Bucket ${b.l}: ${b.p.toFixed(1)}%`}
              style={{ display:'flex', alignItems:'center', gap:3 }}>
              <div style={{
                width: Math.max(4, b.p * 1.2), height: 18, background: b.c,
                borderRadius: 3, minWidth: 4, transition: 'width 0.3s',
              }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: b.c, minWidth: 22 }}>
                {b.l} {b.p.toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}

/* ════════════════════════ Managers' Note Modal ════════════════════════════ */
function ManagersNoteModal({ cycle, onClose }) {
  const S = ({ n, title, children }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          minWidth: 26, height: 26, borderRadius: '50%', background:'#1A2744',
          color:'#fff', fontSize: 11.5, fontWeight: 800, display:'inline-flex',
          alignItems:'center', justifyContent:'center', flexShrink: 0,
        }}>{n}</span>
        <h3 style={{ fontSize: 14, fontWeight: 700, color:'#0F172A', margin: 0, letterSpacing:'-0.01em' }}>
          {title}
        </h3>
      </div>
      <div style={{ paddingLeft: 36 }}>{children}</div>
    </div>
  );

  const Bullets = ({ items }) => (
    <ul style={{ margin:'6px 0', paddingLeft: 20, listStyle:'none' }}>
      {items.map((item, i) => (
        <li key={i} style={{ display:'flex', gap: 8, marginBottom: 5, fontSize: 13, color:'#334155', lineHeight: 1.55 }}>
          <span style={{ color:'#94A3B8', flexShrink: 0, marginTop: 2 }}>•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );

  const SubHead = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 700, color:'#475569', textTransform:'uppercase',
      letterSpacing:'0.07em', marginTop: 12, marginBottom: 5 }}>{children}</div>
  );

  const buckets = [
    { letter:'A', label:'Top Performers / Critical Talent', pct:'12% – 16%', pop:'~15–20%', bg:'#D1FAE5', border:'#6EE7B7', color:'#065F46', num:'#059669' },
    { letter:'B', label:'Strong / Dependable Performers',   pct:'8% – 10%',  pop:'~35–40%', bg:'#DBEAFE', border:'#93C5FD', color:'#1E40AF', num:'#2563EB' },
    { letter:'C', label:'Average Performers',               pct:'4% – 7%',   pop:'~25–30%', bg:'#FEF3C7', border:'#FCD34D', color:'#78350F', num:'#D97706' },
    { letter:'D', label:'Low Performers / Overpaid / Watchlist', pct:'0% – 3%', pop:'~10–15%', bg:'#FEE2E2', border:'#FCA5A5', color:'#7F1D1D', num:'#DC2626' },
  ];

  const groups = [
    {
      title:'Group 1 – Eligible for October 2026 Review',
      border:'#6EE7B7', bg:'#F0FDF4', headColor:'#065F46',
      applicability: ['Borderline performers', 'Improving but not fully delivering', 'Important roles with partial contribution'],
      volume: '~5% of the Department HC',
      treatment: ['0% in April', 'To be reviewed in October 2026'],
    },
    {
      title:'Group 2 – Next Review Only in April 2027',
      border:'#FCA5A5', bg:'#FFF5F5', headColor:'#7F1D1D',
      applicability: ['Low performers', 'Role misalignment', 'Clearly overpaid vs contribution', 'Behaviour concerns'],
      volume: '~5% of the Department HC',
      treatment: ['0% in April', 'No October review', 'Next cycle: April 2027'],
    },
  ];

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, background:'rgba(10,15,35,0.72)',
      backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1001, padding:20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'#fff', borderRadius:18, width:'100%', maxWidth:780,
        maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column',
        boxShadow:'0 32px 80px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.06)',
      }}>

        {/* ── Header ── */}
        <div style={{
          background:'linear-gradient(135deg,#047857 0%,#065F46 100%)',
          padding:'22px 28px',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16,
        }}>
          <div>
            <div style={{ fontSize:19, fontWeight:800, color:'#fff', letterSpacing:'-0.02em',
              display:'flex', alignItems:'center', gap:10 }}>
              <span style={{
                width:36, height:36, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.18)', borderRadius:10,
                display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0,
              }}>📋</span>
              Managers' Note
            </div>
            <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.65)', marginTop:6, fontWeight:500 }}>
              {cycle?.cycle_id
                ? `${cycle.cycle_id} · Increment Cycle`
                : 'April 2026 Increment Cycle'}
            </div>
          </div>
          <button onClick={onClose} style={{
            background:'rgba(255,255,255,0.10)', border:'1px solid rgba(255,255,255,0.18)',
            borderRadius:10, padding:'8px 18px', color:'rgba(255,255,255,0.80)',
            cursor:'pointer', fontSize:13, fontWeight:600, whiteSpace:'nowrap', flexShrink:0,
          }}>✕ &nbsp;Close</button>
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY:'auto', flex:1, padding:'28px 32px' }}>

          <S n={1} title="Purpose of this Note">
            <p style={{ fontSize:13, color:'#475569', lineHeight:1.65, marginBottom:10 }}>
              This note provides guidance on executing the increment cycle, including:
            </p>
            <Bullets items={[
              'Allocation philosophy',
              'Distribution across performance buckets',
              'Treatment of zero-increment cases',
              'Guidance for next-cycle eligibility',
            ]} />
            <p style={{ fontSize:12.5, color:'#64748B', marginTop:10, fontStyle:'italic' }}>
              This is an execution framework for managers handling a large employee base.
            </p>
          </S>

          <S n={2} title="Context for This Cycle">
            <p style={{ fontSize:13, color:'#475569', lineHeight:1.65, marginBottom:8 }}>
              The current cycle is being conducted under challenging business conditions, including:
            </p>
            <Bullets items={[
              'External economic uncertainty',
              'Pressure on margins and pricing',
              'Currency volatility',
              'Increased competition',
              'Extended delivery cycles',
            ]} />
            <div style={{ marginTop:12, padding:'10px 16px', background:'#FFF7ED',
              border:'1px solid #FED7AA', borderRadius:8, fontSize:13, color:'#C2410C', fontWeight:600 }}>
              Accordingly: This will be a disciplined and differentiated increment cycle.
            </div>
          </S>

          <S n={3} title="Overall Increment Framework">
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:180, padding:'14px 18px', background:'#F0FDF4',
                border:'1px solid #86EFAC', borderRadius:10, textAlign:'center' }}>
                <div style={{ fontSize:28, fontWeight:800, color:'#065F46' }}>~8%</div>
                <div style={{ fontSize:12, color:'#047857', fontWeight:600, marginTop:4 }}>Average Increment Target</div>
                <div style={{ fontSize:11.5, color:'#6EE7B7', marginTop:3 }}>Pool average · not an entitlement</div>
              </div>
            </div>
          </S>

          <S n={4} title="Core Allocation Philosophy">
            <p style={{ fontSize:13, color:'#475569', marginBottom:10 }}>Increments must reflect:</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
              {[
                { icon:'🎯', title:'Performance', items:['Quality and consistency of delivery','Contribution to outcomes'] },
                { icon:'⚡', title:'Role Criticality', items:['Impact on revenue, margin, or delivery','Difficulty of replacement'] },
                { icon:'⚖️', title:'Pay Position', items:['Underpaid vs fairly paid vs overpaid'] },
              ].map(c => (
                <div key={c.title} style={{ padding:'12px 14px', background:'#F8FAFC',
                  border:'1px solid #E2E8F0', borderRadius:10 }}>
                  <div style={{ fontSize:18, marginBottom:6 }}>{c.icon}</div>
                  <div style={{ fontSize:12.5, fontWeight:700, color:'#0F172A', marginBottom:6 }}>{c.title}</div>
                  {c.items.map((it,i) => (
                    <div key={i} style={{ fontSize:11.5, color:'#64748B', lineHeight:1.5, marginBottom:3 }}>• {it}</div>
                  ))}
                </div>
              ))}
            </div>
          </S>

          <S n={5} title="Distribution Guidelines">
            <p style={{ fontSize:13, color:'#475569', marginBottom:14 }}>
              Managers must allocate increments across the following buckets:
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {buckets.map(b => (
                <div key={b.letter} style={{ display:'flex', alignItems:'center', gap:14,
                  padding:'12px 16px', background:b.bg, border:`1px solid ${b.border}`, borderRadius:10 }}>
                  <div style={{ width:38, height:38, borderRadius:10, background:b.num,
                    color:'#fff', fontSize:17, fontWeight:800,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {b.letter}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:b.color }}>{b.label}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:15, fontWeight:800, color:b.num }}>{b.pct}</div>
                    <div style={{ fontSize:11, color:b.color, opacity:0.8 }}>Population {b.pop}</div>
                  </div>
                </div>
              ))}
            </div>
          </S>

          <S n={6} title="Expected Distribution (Out of 115 Eligible Employees)">
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#1A2744' }}>
                  {['Category','No. of Employees','Increment Range'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', color:'rgba(255,255,255,0.85)',
                      fontSize:11.5, fontWeight:700, textAlign:'left', letterSpacing:'0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { cat:'Bucket A', bg:'#D1FAE5', border:'#6EE7B7', color:'#065F46', emp:'18 – 22', pct:'12% – 16%' },
                  { cat:'Bucket B', bg:'#DBEAFE', border:'#93C5FD', color:'#1E40AF', emp:'40 – 45', pct:'8% – 10%'  },
                  { cat:'Bucket C', bg:'#FEF3C7', border:'#FCD34D', color:'#78350F', emp:'30 – 35', pct:'4% – 7%'   },
                  { cat:'Bucket D', bg:'#FEE2E2', border:'#FCA5A5', color:'#7F1D1D', emp:'15 – 20', pct:'0% – 3%'   },
                ].map((r, i) => (
                  <tr key={r.cat} style={{ background: i%2===0?'#fff':'#F8FAFC', borderBottom:'1px solid #F1F5F9' }}>
                    <td style={{ padding:'10px 16px' }}>
                      <span style={{ padding:'3px 10px', borderRadius:6, fontSize:12, fontWeight:700,
                        background:r.bg, color:r.color, border:`1px solid ${r.border}` }}>{r.cat}</span>
                    </td>
                    <td style={{ padding:'10px 16px', fontWeight:600, color:'#0F172A' }}>{r.emp}</td>
                    <td style={{ padding:'10px 16px', fontWeight:700, color:r.color }}>{r.pct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </S>

          <S n={7} title="Treatment of Zero-Increment Cases">
            <p style={{ fontSize:13, color:'#475569', marginBottom:14, lineHeight:1.6 }}>
              A subset of employees will not receive increments in this cycle.
              These must be <strong>deliberately categorised</strong>.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {groups.map(g => (
                <div key={g.title} style={{ padding:'16px', background:g.bg,
                  border:`1.5px solid ${g.border}`, borderRadius:12 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:g.headColor, marginBottom:10 }}>{g.title}</div>
                  <SubHead>Applicability</SubHead>
                  <Bullets items={g.applicability} />
                  <SubHead>Volume</SubHead>
                  <div style={{ fontSize:13, color:'#334155', marginBottom:8 }}>{g.volume}</div>
                  <SubHead>Treatment</SubHead>
                  <Bullets items={g.treatment} />
                </div>
              ))}
            </div>
          </S>

          <S n={8} title="Design Principle for Zero-Increment Decisions">
            <div style={{ padding:'12px 16px', background:'#FFF7ED', border:'1px solid #FED7AA',
              borderRadius:8, fontSize:13, color:'#C2410C', fontWeight:600, lineHeight:1.6 }}>
              Zero increments are not uniform — they reflect different underlying reasons.<br />
              <span style={{ fontWeight:400, color:'#92400E' }}>
                Managers must not treat all zero-increment cases the same.
              </span>
            </div>
          </S>

          <S n={9} title="Manager Discipline Guidelines">
            <p style={{ fontSize:13, color:'#475569', marginBottom:8 }}>Managers must ensure:</p>
            <Bullets items={[
              'No clustering of increments in a narrow band',
              'Top bucket does not exceed ~15% of team',
              'Meaningful representation in low / zero bucket',
              'Clear differentiation between B and C buckets',
            ]} />
          </S>

          <S n={10} title="Role of Inflation">
            <Bullets items={[
              'Inflation (~3–3.5%) is a reference point, not a minimum guarantee',
              'Not all employees will receive inflation-linked increases',
            ]} />
          </S>

          <S n={11} title="Use of Variable Pay">
            <Bullets items={[
              'Fixed pay increases are permanent cost additions',
              'Variable pay is the primary lever for performance-linked upside',
            ]} />
            <p style={{ fontSize:12.5, color:'#64748B', marginTop:8 }}>
              Managers should balance both carefully.
            </p>
          </S>

          <S n={12} title="Communication to Employees">
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[
                { label:'For October Review', text:'At this stage, no increment has been provided. Your performance will be reviewed again in the October cycle.' },
                { label:'For Next April',     text:'At this stage, no increment has been provided. The next review will be in the April 2027 cycle.' },
              ].map(c => (
                <div key={c.label} style={{ padding:'12px 16px', background:'#F8FAFC',
                  border:'1px solid #E2E8F0', borderRadius:10 }}>
                  <div style={{ fontSize:11.5, fontWeight:700, color:'#64748B',
                    textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>{c.label}</div>
                  <div style={{ fontSize:13, color:'#0F172A', fontStyle:'italic', lineHeight:1.6 }}>
                    "{c.text}"
                  </div>
                </div>
              ))}
            </div>
          </S>

          <S n={13} title="What Should Not Be Done">
            <Bullets items={[
              'Uniform or equal distribution',
              'Emotional adjustments for perceived fairness',
              'Granting increases without clear justification',
              'Overprotecting weak performers',
            ]} />
          </S>

          <S n={14} title="Final Position">
            <Bullets items={[
              'Retain critical talent',
              'Reward strong performers',
              'Maintain cost discipline',
              'Create clear differentiation',
            ]} />
            <div style={{ marginTop:14, padding:'14px 18px',
              background:'linear-gradient(135deg,#1A2744,#0F172A)',
              borderRadius:10, fontSize:13.5, color:'#fff', fontWeight:600,
              lineHeight:1.65, letterSpacing:'0.01em' }}>
              🎯 Leadership Anchor<br />
              <span style={{ fontWeight:400, color:'rgba(255,255,255,0.75)' }}>
                This is a differentiated cycle — not everyone will receive the same outcome,
                and that is intentional.
              </span>
            </div>
          </S>

        </div>

        {/* ── Footer ── */}
        <div style={{ padding:'10px 28px', borderTop:'1px solid #E5E7EB',
          fontSize:11.5, color:'#94A3B8', background:'#F8FAFC',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>Confidential — For Managers Only</span>
          <span>{cycle?.cycle_id || 'April 2026 Increment Cycle'}</span>
        </div>
      </div>
    </div>
  );
}
